import React, { useContext, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material'
import { alpha, styled } from '@mui/material/styles'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import { usePatternContext } from '../contexts/PatternContext'
import useSaveCustomIndicator from '../../../hooks/useSaveCustomIndicator'
import { CustomIndicatorDoc } from '../../../types/composite'
import { collectCompositeIdsFromPattern } from '../../../common/utils/composite'

// Card-surface row matching the PatternRow look
const IndicatorRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.background.custom2,
  border: `1px solid ${theme.palette.background.custom4}`,
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 0.25s ease',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.5),
    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
}))

// Mirrors PatternListView's NoPatternMessage empty state
const NoIndicatorMessage = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  textAlign: 'center',
  background: theme.palette.background.custom1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  boxShadow: 'none',
}))

const MyIndicatorsList: React.FC = () => {
  const { usermeta, tradingHouse } = useApplicationSuiteContext()
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)
  const { customIndicators, customIndicatorsError, allPatterns, setEditingIndicator, searchTerm } =
    usePatternContext()
  const { publish, createIndicator, setActive, setVisibility } = useSaveCustomIndicator({
    gcid: usermeta?.gcid,
    tradingHouse,
  })

  const [menu, setMenu] = useState<{ anchor: HTMLElement; doc: CustomIndicatorDoc } | null>(null)
  // Double-click/double-tap guard: duplicate is non-idempotent (each write
  // creates another "… copy" doc), toggles just get the same protection free
  const actionPendingRef = useRef(false)

  const runOnce = (action: () => Promise<unknown>) => {
    if (actionPendingRef.current) return
    actionPendingRef.current = true
    action().finally(() => {
      actionPendingRef.current = false
    })
  }

  // allPatterns, not the search-filtered list — a stale search term must not
  // zero the counts and skip the deactivate warning
  const usageCounts = useMemo(() => {
    const counts = new Map<string, number>()
    ;(allPatterns ?? []).forEach((pattern: any) => {
      collectCompositeIdsFromPattern(pattern).forEach((id) =>
        counts.set(id, (counts.get(id) ?? 0) + 1)
      )
    })
    return counts
  }, [allPatterns])

  const sorted = useMemo(
    () =>
      customIndicators
        .filter((ci) => !searchTerm || ci.name.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort((a, b) => Number(b.active) - Number(a.active)),
    [customIndicators, searchTerm]
  )

  const closeMenu = () => setMenu(null)

  // Create-on-entry (pattern parity): v1 exists before the editor opens
  const handleCreate = () => {
    runOnce(() =>
      createIndicator()
        .then((saved) => setEditingIndicator({ editId: saved.id, doc: saved }))
        .catch((error: any) =>
          setSnackbarSettings({
            severity: 'error',
            message: error?.message ?? 'Failed to create indicator',
          })
        )
    )
  }

  const handleDuplicate = (doc: CustomIndicatorDoc) => {
    closeMenu()
    runOnce(() =>
      publish({
        name: `${doc.name} copy`,
        nodes: doc.def.nodes,
        outputs: doc.def.outputs,
        scale: doc.def.scale ?? 'oscillator',
      })
        .then((saved) =>
          setSnackbarSettings({ severity: 'success', message: `${saved.name} created` })
        )
        .catch((error: any) =>
          setSnackbarSettings({
            severity: 'error',
            message: error?.message ?? 'Failed to duplicate indicator',
          })
        )
    )
  }

  const handleToggleVisibility = (doc: CustomIndicatorDoc) => {
    closeMenu()
    const makingPublic = doc.visibility !== 'public'
    if (
      makingPublic &&
      !window.confirm(
        `Make "${doc.name}" public? Other users will be able to see its implementation details (steps, math, and parameters).`
      )
    ) {
      return
    }
    runOnce(() =>
      setVisibility(doc.id, makingPublic ? 'public' : 'private').catch((error: any) =>
        setSnackbarSettings({
          severity: 'error',
          message: error?.message ?? 'Failed to update indicator visibility',
        })
      )
    )
  }

  const handleToggleActive = (doc: CustomIndicatorDoc) => {
    closeMenu()
    const usage = usageCounts.get(doc.id) ?? 0
    if (
      doc.active &&
      usage > 0 &&
      !window.confirm(
        `Deactivate "${doc.name}"? Patterns using it keep working; it disappears from the indicator picker.`
      )
    ) {
      return
    }
    runOnce(() =>
      setActive(doc.id, !doc.active).catch((error: any) =>
        setSnackbarSettings({
          severity: 'error',
          message: error?.message ?? 'Failed to update indicator',
        })
      )
    )
  }

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <Typography variant="body2" color="text.secondary">
        Build indicators from chained steps, then use them anywhere an indicator can be selected.
      </Typography>

      {!!customIndicatorsError && (
        <Alert severity="error">
          Couldn't load your custom indicators — try again in a moment.
        </Alert>
      )}

      {sorted.length === 0 && !customIndicatorsError && (
        <NoIndicatorMessage>
          <Typography variant="h6">No Indicators Found</Typography>
          <Typography color="text.secondary" maxWidth="600px">
            Custom indicators chain standard indicators and math over chart data into new values you
            can use in any pattern condition. Create a new indicator to get started.
          </Typography>
          <Button variant="contained" color="primary" onClick={handleCreate}>
            Create New Indicator
          </Button>
        </NoIndicatorMessage>
      )}

      <Box display="flex" flexDirection="column" gap={1}>
        {sorted.map((doc) => {
          const usage = usageCounts.get(doc.id) ?? 0
          return (
            <IndicatorRow key={doc.id} onClick={() => setEditingIndicator({ editId: doc.id })}>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: 'common.white',
                      fontWeight: 600,
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {doc.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={`v${doc.latestVersion}`}
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
                  />
                  {!doc.active && (
                    <Chip
                      size="small"
                      label="Inactive"
                      color="warning"
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
                    />
                  )}
                  {doc.visibility === 'public' && (
                    <Chip
                      size="small"
                      label="Public"
                      color="info"
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                  {(doc.def.scale ?? 'oscillator') === 'overlay' ? 'Overlay' : 'Oscillator'} ·{' '}
                  {doc.def.outputs.length} output{doc.def.outputs.length === 1 ? '' : 's'} · used by{' '}
                  {usage} pattern{usage === 1 ? '' : 's'}
                </Typography>
              </Box>

              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  setMenu({ anchor: e.currentTarget, doc })
                }}
                sx={{ flexShrink: 0, opacity: 0.5, '&:hover': { opacity: 1 } }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </IndicatorRow>
          )
        })}
      </Box>

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            closeMenu()
            if (menu) setEditingIndicator({ editId: menu.doc.id })
          }}
        >
          Edit
        </MenuItem>
        <MenuItem onClick={() => menu && handleDuplicate(menu.doc)}>Duplicate</MenuItem>
        <MenuItem onClick={() => menu && handleToggleVisibility(menu.doc)}>
          {menu?.doc.visibility === 'public' ? 'Make private' : 'Make public'}
        </MenuItem>
        <MenuItem onClick={() => menu && handleToggleActive(menu.doc)}>
          {menu?.doc.active ? 'Deactivate' : 'Reactivate'}
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default MyIndicatorsList
